import React, { FC } from 'react';

import { Link } from 'react-router-dom';

import Utils from '../utils';
import MetadataUtils from '../utils/utils-metadata';
import { Author } from '../../../models/Product';
import { useLanguage } from '../../contexts/language';
import { useTranslation } from 'react-i18next';

type Props = {
    readonly product?: {
        productClass?: string;
        authors?: ReadonlyArray<Author>;
    };
    readonly showAsTile?: boolean;
    readonly linked?: boolean;
    readonly loanFormatKey: string | null | undefined;
    readonly onMouseEnter?: () => void;
};

export const ProductAuthors: FC<Props> = ({
    product = {},
    loanFormatKey,
    showAsTile = false,
    linked = true,
    onMouseEnter
}) => {
    const { productClass, authors } = product;
    const language = useLanguage();
    const { t } = useTranslation();

    if (!authors || authors.length < 1) {
        return null;
    }

    // TODO: extract a separate component for this use case.
    //  It seems to be used on the product details page.
    if (showAsTile) {
        return (
            <div className="authors">
                {authors.map(author => {
                    const performerPhotoUrl = Utils.performerPhotoUrl(author.id);
                    const linkTo = setAuthorLink(productClass, author, linked, loanFormatKey, language);
                    return (
                        <Link key={author.id} className="person" to={linkTo ?? '#'}>
                            <div className="photo" style={{ backgroundImage: `url(${performerPhotoUrl})` }} />
                            <div className="info">
                                <span className="title">{t('components.productDetails.author')}</span>
                                <span className="name">{Utils.getPerformerFullname(author)}</span>
                            </div>
                        </Link>
                    );
                })}
            </div>
        );
    }

    const filteredAuthors = Utils.filterUntilTextLength(authors, {
        getTextFunction: author => {
            return `${Utils.getPerformerFullname(author)}, `;
        }
    });

    const allShown = filteredAuthors.length === authors.length;

    return (
        <p onMouseEnter={onMouseEnter}>
            {Utils.safeJoinComponents(
                filteredAuthors.map(author => {
                    const linkTo = setAuthorLink(productClass, author, linked, loanFormatKey, language);
                    return linkTo ? (
                        <Link key={author.id} to={linkTo}>
                            {Utils.getPerformerFullname(author)}
                        </Link>
                    ) : (
                        Utils.getPerformerFullname(author)
                    );
                }),
                ', ',
                'span'
            )}
            {!allShown ? ',...' : null}
        </p>
    );
};

export default ProductAuthors;

function setAuthorLink(productClass, author, isLinked, loanFormatKey, language) {
    return isLinked
        ? MetadataUtils.createMetaDataLink(
              productClass,
              { author: { include: [author.id] } },
              { loanFormatKey, language }
          )
        : null;
}
